import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Field, useFormikContext } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import {
  FormGroup,
  TextboxField,
  Modal,
  SelectDropdownField,
  triggerUspsVerification,
} from '@tradingblock/components';
import { validateAddressFields, getUspsResponseFromStorage } from './addressValidation';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import {
  AddressUpdateValueProps,
  AllStates,
  Country,
  getGroupedCountryOptions,
  State,
  State as StateEnum,
} from '@tradingblock/types';
import { useClearer } from '../../../hooks/useClearer';
import Config from '../../../config';
import { apiTokenSelector } from '../../../data/global/dataSelectors';

interface AddressUpdateProps {
  show?: boolean;
  toggleModal: () => void;
  addressType: 'physical' | 'mailing';
  accountHolderIndex: number;
  accountDetails: {
    physicalAddress: {
      address1: string;
      address2?: string;
      city: string;
      country: string;
      postalCode: string;
      state: string;
    };
  };
}

export const AddressUpdate: React.FunctionComponent<AddressUpdateProps> = ({
  show,
  toggleModal,
  addressType,
  accountHolderIndex,
  accountDetails,
}) => {
  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const clearer = useClearer();
  const dispatch = useDispatch();
  const { accountHolders } = useStateSelector(s => s.accountManagement.accountManagementDetails.details);
  const isLoading = useStateSelector(s => s.accountManagement.update.isLoading);
  const apiToken = useStateSelector(apiTokenSelector);
  const user = accountHolders[accountHolderIndex];

  const header = useMemo(() => {
    switch (addressType) {
      case 'physical':
        return 'Physical Address';
      case 'mailing':
        return 'Mailing Address';
      default:
        return '';
    }
  }, [addressType]);

  const initialAddressUpdateValues: AddressUpdateValueProps = useMemo(
    () => ({
      address1:
        addressType === 'physical'
          ? user.address.address1 || ''
          : user.mailingAddress && user.mailingAddress.address1
            ? user.mailingAddress.address1
            : '',
      address2:
        addressType === 'physical'
          ? user.address.address2 || ''
          : user.mailingAddress && user.mailingAddress.address2
            ? user.mailingAddress.address2
            : '',
      city:
        addressType === 'physical'
          ? user.address.city || ''
          : user.mailingAddress && user.mailingAddress.city
            ? user.mailingAddress.city
            : '',
      state:
        addressType === 'physical'
          ? user.address.state || ''
          : user.mailingAddress && user.mailingAddress.state
            ? user.mailingAddress.state
            : '',
      postalCode:
        addressType === 'physical'
          ? user.address.postalCode || ''
          : user.mailingAddress && user.mailingAddress.postalCode
            ? user.mailingAddress.postalCode
            : '',
      country:
        addressType === 'physical'
          ? user.address.country || ''
          : user.mailingAddress && user.mailingAddress.country
            ? user.mailingAddress.country
            : '',
      addressType: '',
    }),
    [addressType, user]
  );

  const getOtherAddressValues = useMemo(() => {
    if (addressType === 'physical' && user.mailingAddress) {
      return {
        address1: user.mailingAddress.address1 || '',
        address2: user.mailingAddress.address2 || '',
        city: user.mailingAddress.city || '',
        state: user.mailingAddress.state || '',
        postalCode: user.mailingAddress.postalCode || '',
        country: user.mailingAddress.country || '',
        addressType,
      };
    } else if (addressType === 'mailing' && user.address) {
      return {
        address1: user.address.address1 || '',
        address2: user.address.address2 || '',
        city: user.address.city || '',
        state: user.address.state || '',
        postalCode: user.address.postalCode || '',
        country: user.address.country || '',
        addressType,
      };
    }
    return null;
  }, [addressType, user]);

  const shouldShowCopyButton = useMemo(() => {
    if (!user) return false;

    const currentAddress = addressType === 'physical' ? user.address : user.mailingAddress;
    const otherAddress = addressType === 'physical' ? user.mailingAddress : user.address;

    if (!currentAddress || !otherAddress) return false;

    // Compare all address fields
    return (
      currentAddress.address1 !== otherAddress.address1 ||
      currentAddress.address2 !== otherAddress.address2 ||
      currentAddress.city !== otherAddress.city ||
      currentAddress.state !== otherAddress.state ||
      currentAddress.postalCode !== otherAddress.postalCode ||
      currentAddress.country !== otherAddress.country
    );
  }, [user, addressType]);

  const onValidate = useCallback(
    (values: AddressUpdateValueProps) => {
      const headerValue = addressType === 'physical' ? 'physical' : 'mailing';
      const storageKey = `addressVerification-${headerValue}-${accountHolderIndex}UspsResponse`;
      const isMailing = addressType === 'mailing';

      // Use shared address validation function
      // Note: triggerUspsVerification is false here because we handle API calls separately in useEffect
      // This prevents duplicate API calls (one from validation, one from useEffect)
      return validateAddressFields(
        {
          address1: values.address1,
          address2: values.address2,
          city: values.city,
          state: values.state,
          postalCode: values.postalCode,
          country: values.country,
        },
        {
          storageKey,
          apiToken,
          isMailing,
          triggerUspsVerification: false,
        }
      );
    },
    [addressType, accountHolderIndex, apiToken]
  );

  const onSubmit = useCallback(
    (values: AddressUpdateValueProps, { resetForm }) => {
      if (!accountId) return;

      const { address1, address2, city, country, postalCode, state } = values;
      const updateValues = {
        item: addressType === 'physical' ? 'Address' : 'MailingAddress',
        accountHolder: {
          [addressType === 'physical' ? 'address' : 'mailingAddress']: {
            address1,
            address2,
            city,
            country,
            postalCode,
            state,
          },
        },
        holder: accountHolderIndex,
      };

      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId,
          request: updateValues,
        })
      );
      resetForm();
      toggleModal();
    },
    [accountId, addressType, accountHolderIndex, dispatch, toggleModal]
  );

  if (!accountId || !user) {
    return null;
  }

  return (
    <Formik
      initialValues={initialAddressUpdateValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnChange={true}
    >
      {({ handleSubmit, resetForm, values, errors, setValues, validateForm }) => (
        <AddressUpdateFormContent
          handleSubmit={handleSubmit}
          resetForm={resetForm}
          values={values}
          errors={errors}
          setValues={setValues}
          validateForm={validateForm}
          header={header}
          addressType={addressType}
          shouldShowCopyButton={shouldShowCopyButton}
          getOtherAddressValues={getOtherAddressValues}
          isLoading={isLoading}
          toggleModal={toggleModal}
          clearer={clearer}
          accountHolderIndex={accountHolderIndex}
          show={show}
        />
      )}
    </Formik>
  );
};

// Inner component to access Formik context for debounced validation
const AddressUpdateFormContent: React.FC<{
  handleSubmit: () => void;
  resetForm: () => void;
  values: AddressUpdateValueProps;
  errors: any;
  setValues: (values: AddressUpdateValueProps) => void;
  validateForm: () => void;
  header: string;
  addressType: 'physical' | 'mailing';
  shouldShowCopyButton: boolean;
  getOtherAddressValues: AddressUpdateValueProps | null;
  isLoading: boolean;
  toggleModal: () => void;
  clearer: any;
  accountHolderIndex: number;
  show?: boolean;
}> = ({
  handleSubmit,
  resetForm,
  values,
  errors,
  setValues,
  validateForm,
  header,
  addressType,
  shouldShowCopyButton,
  getOtherAddressValues,
  isLoading,
  toggleModal,
  clearer,
  accountHolderIndex,
  show,
}) => {
    const context = useFormikContext<AddressUpdateValueProps>();
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const apiToken = useStateSelector(apiTokenSelector);

    // Debounced USPS validation effect
    useEffect(() => {
      const headerValue = addressType === 'physical' ? 'physical' : 'mailing';
      // Use a prefix that will result in the storage key: addressVerification-{type}-{index}UspsResponse
      // triggerUspsVerification stores to: ${prefix.split('.')[0]}UspsResponse
      // So we use the full key as prefix (without UspsResponse suffix)
      const prefix = `addressVerification-${headerValue}-${accountHolderIndex}`;
      const customStorageKey = `${prefix}UspsResponse`;
      const validateAddressStorageKey = 'addressUspsResponse';

      // Get current address values
      const { address1, address2, city, state, postalCode, country } = values;

      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set up debounced validation (500ms)
      debounceTimerRef.current = setTimeout(async () => {
        // Check if validation should run (US country, required fields present, not Puerto Rico)
        if (
          address1 &&
          city &&
          state &&
          country === Country.UnitedStatesOfAmerica &&
          state !== StateEnum.PuertoRico &&
          Config.tradingApi
        ) {
          try {
            // Check cache first to avoid unnecessary API calls
            const cachedResponse = sessionStorage.getItem(customStorageKey);
            if (cachedResponse && cachedResponse !== 'undefined') {
              try {
                const cached = JSON.parse(cachedResponse);
                // Check if cached result matches current address
                const normalize = (str: string) => (str || '').trim().toUpperCase();
                if (
                  cached.uspsAddress1 &&
                  normalize(cached.uspsAddress1) === normalize(address1) &&
                  cached.city &&
                  normalize(cached.city) === normalize(city) &&
                  cached.state &&
                  normalize(cached.state) === normalize(state)
                ) {
                  // Cache matches - no need to call API
                  // Sync to validateAddress storage key
                  sessionStorage.setItem(validateAddressStorageKey, cachedResponse);
                  // Trigger Formik validation to update error states
                  context.validateForm();
                  return;
                }
              } catch (e) {
                // Invalid cache, continue to make API call
              }
            }

            const address = {
              address1,
              address2,
              city,
              state,
              postalCode,
              country,
            };

            const result = await triggerUspsVerification(address, prefix, Config.tradingApi, apiToken);

            // Sync the result to the key format that validateAddress expects
            // triggerUspsVerification stores to: ${prefix.split('.')[0]}UspsResponse = addressVerification-physical-0UspsResponse
            // validateAddress with prefix 'address.' looks for: addressUspsResponse
            if (result) {
              // Store to both keys to keep them in sync
              sessionStorage.setItem(customStorageKey, JSON.stringify(result));
              sessionStorage.setItem(validateAddressStorageKey, JSON.stringify(result));
            }

            // Trigger Formik validation to update error states
            context.validateForm();
          } catch (err) {
            console.error('Error in debounced USPS validation:', err);
          }
        } else if ((!address1 || !city) && Config.tradingApi) {
          // Clear USPS storage if fields are cleared
          sessionStorage.setItem(customStorageKey, 'undefined');
          sessionStorage.setItem(validateAddressStorageKey, 'undefined');
        }
      }, 500);

      // Cleanup function
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      values.address1,
      values.address2,
      values.city,
      values.state,
      values.postalCode,
      values.country,
      addressType,
      accountHolderIndex,
      apiToken,
    ]);

    return (
      <Modal setShow={toggleModal} show={show}>
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Address Update</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <h6>{header}</h6>
          <form id="addressUpdate" onSubmit={handleSubmit}>
            <FormGroup>
              <Field
                component={SelectDropdownField}
                name="country"
                id="country"
                label="Country"
                options={getGroupedCountryOptions(clearer)}
                optionsDefaultLabelId="country"
              />
            </FormGroup>
            <FormGroup>
              <Field component={TextboxField} name="address1" id="address1" label="Address 1" />
            </FormGroup>
            <FormGroup>
              <Field component={TextboxField} name="address2" id="address2" label="Address 2" />
            </FormGroup>
            <FormGroup>
              <Field component={TextboxField} name="city" id="city" label="City" />
            </FormGroup>
            <FormGroup>
              {values.country === Country.UnitedStatesOfAmerica && (
                <Field
                  component={SelectDropdownField}
                  name="state"
                  id="state"
                  label="State"
                  options={AllStates}
                  optionsDefaultLabelId="state"
                />
              )}
              {values.country !== Country.UnitedStatesOfAmerica && values.country !== '' && (
                <Field component={TextboxField} name="state" id="state" label="Province" />
              )}
            </FormGroup>
            <FormGroup>
              <Field component={TextboxField} name="postalCode" id="postalCode" label="Postal Code" />
            </FormGroup>
            {(() => {
              // Get USPS response from storage using shared helper
              const headerValue = addressType === 'physical' ? 'physical' : 'mailing';
              const storageKey = `addressVerification-${headerValue}-${accountHolderIndex}UspsResponse`;
              const uspsAddress = getUspsResponseFromStorage(storageKey);

              // Check if there are USPS formatting errors
              const hasUspsErrors =
                errors &&
                uspsAddress &&
                ((uspsAddress.address1 && errors.address1 && errors.address1.includes('USPS provided formatting')) ||
                  (uspsAddress.address2 && errors.address2 && errors.address2.includes('USPS provided formatting')) ||
                  (uspsAddress.city && errors.city && errors.city.includes('USPS Provided')) ||
                  (uspsAddress.state && errors.state && errors.state.includes('USPS Provided')) ||
                  (uspsAddress.postalCode && errors.postalCode && errors.postalCode.includes('USPS Provided')));

              if (!hasUspsErrors || !uspsAddress) {
                return null;
              }

              return (
                <FormGroup>
                  <button
                    className="btn btn-block btn-outline-warning"
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      const updatedValues = { ...values };
                      if (uspsAddress.address1) updatedValues.address1 = uspsAddress.address1;
                      // If USPS provides address2 separately, use it; otherwise clear it (USPS combined into address1)
                      if (uspsAddress.address2) {
                        updatedValues.address2 = uspsAddress.address2;
                      } else {
                        updatedValues.address2 = '';
                      }
                      if (uspsAddress.city) updatedValues.city = uspsAddress.city;
                      if (uspsAddress.state) updatedValues.state = uspsAddress.state;
                      if (uspsAddress.postalCode) updatedValues.postalCode = uspsAddress.postalCode;
                      setValues(updatedValues);
                      setTimeout(() => {
                        validateForm();
                      }, 100);
                    }}
                  >
                    Use USPS provided address
                  </button>
                </FormGroup>
              );
            })()}
          </form>
          {addressType === 'mailing' && (
            <p className="txt-sm mute">
              All updates are pending admin review. You will be notified when your submission has been processed.
            </p>
          )}
          {shouldShowCopyButton && getOtherAddressValues && (
            <Button
              variant="secondary"
              className="mb-3"
              onClick={() => {
                const otherAddress = getOtherAddressValues;
                if (otherAddress) {
                  setValues(otherAddress);
                }
              }}
            >
              Copy from {addressType === 'physical' ? 'Mailing' : 'Physical'} Address
            </Button>
          )}
        </BootstrapModal.Body>
        <BootstrapModal.Footer className="modal-footer-justified">
          <Button
            variant="secondary"
            onClick={() => {
              resetForm();
              toggleModal();
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleSubmit()} disabled={isLoading}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </BootstrapModal.Footer>
      </Modal>
    );
  };
